import { DealFinderResultsState } from '@model/state/deal-finder-results-state';
import { DealFinderState, Destination } from '@model/state';
import { Page } from '@model/common';
import {
  performNavigate,
  RequestType,
  setLoading,
  setPackageReferences,
  unsetPackageReferences
} from '@state/slices/app';
import { getToursToken } from '@state/slices/search-results/tours/toursSlice';
import {
  DestinationParams,
  PackageResponse,
  PackagesParams,
  PackagesResponse,
  Result,
  Results,
  SearchCriteria,
  SearchType
} from '@model/iceberg/deal-finder/deal-finder';
import { DealFinderApi } from '@model/iceberg/service/deal-finder/deal-finder-api';
import { DealFinderMapper, mapChildrenStringToDob } from '@model/iceberg/service/deal-finder/deal-finder-mapper';
import { Logging } from '@util/logging';
import { FiltersAdapter } from '@model/iceberg/filters/deal-finder/filters-adapter';
import { isHotelPath } from '@util/path';
import { getDefaultSearchMonth, isValidSearchDate } from '@util/date-time';
import Cookies, { Cookie } from '@model/common/cookie/cookie';
import { getDealFinderUrl } from '@util/deal-finder';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MOCK_SKELETON_LOADING_PACKAGES_RESPONSE } from '@mock/iceberg/deal-finder/packages';

interface ApiResponse<T> {
  response?: T;
  error?: Error;
}

export enum DealFinderResultsActions {
  NAME = '@DEAL_FINDER_RESULTS',
  PERFORM_SEARCH = '@DEAL_FINDER_RESULTS/PERFORM_SEARCH',
  PERFORM_DESTINATION_SEARCH = '@DEAL_FINDER_RESULTS/PERFORM_DESTINATION_SEARCH',
  PERFORM_PRODUCT_SEARCH = '@DEAL_FINDER_RESULTS/PERFORM_PRODUCT_SEARCH',
  SEARCH_FAILURE = '@DEAL_FINDER_RESULTS/SEARCH_FAILURE',
  RECEIVE_TOKEN_SUCCESS = '@DEAL_FINDER_RESULTS/receiveTokenSuccess',
  RECEIVE_RESULTS_SUCCESS = '@DEAL_FINDER_RESULTS/receiveResultsSuccess',
  RECEIVE_RESULTS_FAILURE = '@DEAL_FINDER_RESULTS/receiveResultsFailure',
  LOAD_MORE = '@DEAL_FINDER/LOAD_MORE',
  SET_FILTERS = '@DEAL_FINDER/SET_FILTERS',
  TOKEN_EXPIRED = '@DEAL_FINDER_RESULTS/tokenExpired',
  CLEAR_RESULTS = '@DEAL_FINDER_RESULTS/clearResults',
  SET_TOKEN = '@DEAL_FINDER_RESULTS/setToken',
  GET_PACKAGES = '@DEAL_FINDER_RESULTS/GET_PACKAGES'
}

export const INITIAL_DEAL_FINDER_RESULTS_STATE: DealFinderResultsState = {
  data: {
    searchToken: '',
    expires: '',
    results: [],
    facets: {
      filters: [],
      counts: {
        total: 0,
        filtered: 0
      }
    },
    nextSet: 0
  },
  loading: false,
  error: false
};

export const tokenExpired = () => ({ type: DealFinderResultsActions.TOKEN_EXPIRED });

export const callGetPackage = async (payload: SearchCriteria): Promise<ApiResponse<PackageResponse>> => {
  const api: DealFinderApi = new DealFinderApi();
  const marketingCode = new Cookies().get(Cookie.MARKETING_CODE);

  try {
    const response = await api.package({ ...payload, marketingCode });
    return { response };
  } catch (error) {
    Logging.error({ text: error instanceof Error ? error.message : String(error) });
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }
};

export const callGetPackages = async (payload: PackagesParams): Promise<ApiResponse<PackagesResponse>> => {
  const api: DealFinderApi = new DealFinderApi();
  try {
    const response = await api.packages(payload);
    return { response };
  } catch (error) {
    Logging.error({ text: error instanceof Error ? error.message : String(error) });
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }
};

export const performSearch = createAsyncThunk(
  DealFinderResultsActions.PERFORM_SEARCH,
  async (searchParams: DealFinderState, { dispatch, rejectWithValue }) => {
    const destinations: Array<Destination> = searchParams.destinations;
    const { month: payloadMonth, date, ...nonDateParams } = searchParams;
    const [year, month, day] = (payloadMonth || date || '').split('-');

    const searchPayload =
      !year || !month || isValidSearchDate({ year, month, day, wholeMonth: !date && !!payloadMonth })
        ? searchParams
        : { ...nonDateParams, month: getDefaultSearchMonth(), date: '' };

    const isProductSearch: boolean = destinations.length === 1 && isHotelPath(destinations[0].path);

    dispatch(unsetPackageReferences());

    if (isProductSearch) {
      return await dispatch(performProductSearch(searchParams)).unwrap();
    }

    const payload: SearchCriteria = DealFinderMapper.fromDealFinder(searchPayload);
    const { response, error } = await callGetPackage(payload);

    if (error) {
      return rejectWithValue(error.message);
    }

    if (!response) {
      return rejectWithValue('No response received');
    }

    // Store the token
    dispatch(receiveTokenSuccess(response));

    // Now fetch the actual packages
    const packagesResponse = await callGetPackages({ searchToken: response.searchToken });

    if (packagesResponse.response) {
      const results = packagesResponse.response.data;
      if (results.results.length) {
        // Dispatch success with actual results
        dispatch(receiveResultsSuccess(results));
        return results; // Return results, not token
      } else {
        dispatch(receiveResultsFailure());
        return rejectWithValue('No results found');
      }
    } else {
      dispatch(receiveResultsFailure());
      return rejectWithValue(packagesResponse.error?.message || 'Failed to fetch packages');
    }
  }
);

export const performDestinationSearch = createAsyncThunk(
  DealFinderResultsActions.PERFORM_DESTINATION_SEARCH,
  async (params: DestinationParams, { rejectWithValue }) => {
    const api = new DealFinderApi();
    const response = await api.destination(params);
    if (!response) {
      return rejectWithValue('No response received');
    }

    const packagesResponse = await api.packages(response);

    if (!packagesResponse) {
      return null;
    }

    try {
      if (packagesResponse) {
        if (packagesResponse.data.results.length) {
          return packagesResponse.data;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error: any) {
      Logging.error({
        text: error.message || error
      });
      return rejectWithValue(error.message);
    }
  }
);

export const performProductSearch = createAsyncThunk(
  DealFinderResultsActions.PERFORM_PRODUCT_SEARCH,
  async (searchParams: DealFinderState, { dispatch, rejectWithValue }) => {
    const payload: SearchCriteria = DealFinderMapper.fromDealFinder(searchParams);
    const destinations: Array<Destination> = searchParams.destinations;
    const hotel: Destination = destinations[0];

    const api = new DealFinderApi();
    const { response } = await callGetPackage(payload);

    try {
      if (response) {
        const { searchToken } = response;
        const packages: Results | null = await dispatch(getPackages(searchToken)).unwrap();

        if (packages && packages.results.length > 0) {
          const result = packages.results[0];
          const packageReferences: Array<string> = result.leadInPrice?.packageReferences || [];
          const { searchType, dateFrom, month, duration, from, occupancy, preferredBoardBasis } = payload;
          const progressDisallowed = searchType === SearchType.HOTEL_ONLY && !result.hotelOnly;

          if (packageReferences.length && !progressDisallowed) {
            dispatch(setPackageReferences(packageReferences));
            await api.reference({ searchToken, packageReferences });

            const dealFinderUrl = getDealFinderUrl({
              token: searchToken,
              path: hotel.path,
              page: Page.PRODUCT,
              from,
              duration,
              occupancy: mapChildrenStringToDob(occupancy),
              date: dateFrom || month || '',
              searchType,
              preferredBoardBasis: preferredBoardBasis || ''
            });

            dispatch(performNavigate(dealFinderUrl));
          }
        }
      }
      return response;
    } catch (error: any) {
      dispatch(searchFailure(error));
      Logging.error({
        text: error.message || error
      });
      return rejectWithValue(error.message);
    }
  }
);

export const loadMore = createAsyncThunk(
  DealFinderResultsActions.LOAD_MORE,
  async (_, { getState, dispatch, rejectWithValue }) => {
    dispatch(setLoading({ loading: true, request: RequestType.LOAD_MORE_RESULTS }));
    const state = getState() as any;
    const {
      searchToken,
      nextSet,
      results = [], // Default to empty array
      facets: { filters }
    } = state.dealFinderResults.data;

    const query: Record<string, string> = FiltersAdapter.getQuery(filters);
    const { response } = await callGetPackages({ searchToken, nextSet, query });

    try {
      if (response && response.data) {
        const nextSet: Array<Result> = response.data.results || [];
        const update: Array<Result> = (results || []).concat(nextSet);
        const updatedData = {
          ...response.data,
          results: update
        };
        dispatch(receiveResultsSuccess(updatedData));
        dispatch(setLoading({ loading: false, request: RequestType.LOAD_MORE_RESULTS }));
        return updatedData;
      }
    } catch (error: any) {
      dispatch(receiveResultsFailure());
      Logging.error({
        text: error.message || error
      });
      return rejectWithValue(error.message);
    }
  }
);

export const getPackages = createAsyncThunk(
  DealFinderResultsActions.GET_PACKAGES,
  async (searchToken: string, { dispatch }) => {
    const { response } = await callGetPackages({ searchToken });
    if (response) {
      if (response.data.results.length) {
        dispatch(receiveResultsSuccess({ ...response.data }));
        return response.data;
      } else {
        dispatch(receiveResultsFailure());
        return null;
      }
    } else {
      dispatch(receiveResultsFailure());
      return null;
    }
  }
);

export const setFilters = createAsyncThunk(
  DealFinderResultsActions.SET_FILTERS,
  async (query: Record<string, string>, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as any;
    const { searchToken } = state.dealFinderResults.data;

    const { response } = await callGetPackages({ searchToken, query });
    try {
      if (response) {
        dispatch(receiveResultsSuccess({ ...response.data }));
        return response.data;
      }
    } catch (error: any) {
      Logging.error({
        text: error.message || error
      });
      return rejectWithValue(error.message);
    }
  }
);

const dealFinderResultsSlice = createSlice({
  name: DealFinderResultsActions.NAME,
  initialState: INITIAL_DEAL_FINDER_RESULTS_STATE,
  reducers: {
    clearResults: () => ({
      ...INITIAL_DEAL_FINDER_RESULTS_STATE,
      data: {
        ...MOCK_SKELETON_LOADING_PACKAGES_RESPONSE.data,
        results: MOCK_SKELETON_LOADING_PACKAGES_RESPONSE.data?.results || []
      },
      loading: true
    }),
    setToken: (state, action: PayloadAction<string>) => {
      state.data.searchToken = action.payload;
    },
    searchFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload || true;
      state.loading = false;
    },
    receiveTokenSuccess: (state, action: PayloadAction<PackageResponse>) => {
      state.data.searchToken = action.payload.searchToken;
      state.data.expires = action.payload.expires;
    },
    receiveResultsSuccess: (state, action: PayloadAction<Results>) => {
      // Preserve the token while updating results
      state.data = {
        ...action.payload,
        searchToken: state.data.searchToken || action.payload.searchToken,
        expires: state.data.expires || action.payload.expires
      };
      state.loading = false;
      state.error = false;
    },
    receiveResultsFailure: {
      reducer: (state, action: PayloadAction<Results | undefined>) => {
        state.data = {
          ...state.data,
          ...(action.payload || {}),
          results: action.payload?.results || [],
          searchToken: state.data.searchToken,
          expires: state.data.expires
        };
        state.error = true;
        state.loading = false;
      },
      prepare: (payload?: Results) => ({ payload })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearch.fulfilled, (state, action) => {
        // Set the data from the fulfilled action
        if (action.payload) {
          state.data = {
            ...state.data,
            ...action.payload
          };
        }
        state.loading = false;
      })
      .addCase(performProductSearch.fulfilled, (state, action: PayloadAction<any>) => {
        state.data.searchToken = action.payload.searchToken;
        state.data.expires = action.payload.expires;
        if (action.payload.packages) {
          state.data.results = action.payload.packages.results;
          state.data.facets = action.payload.packages.facets;
          state.data.nextSet = action.payload.packages.nextSet;
        }
        state.loading = false;
      })
      .addCase(performDestinationSearch.fulfilled, (state, action: PayloadAction<Results | null>) => {
        if (action.payload?.results?.length) {
          state.data = {
            ...state.data,
            ...action.payload,
            results: action.payload.results || []
          };
        } else {
          state.error = true;
        }
        state.loading = false;
      })
      .addCase(getPackages.fulfilled, (state, action: PayloadAction<Results | null>) => {
        if (action.payload) {
          state.data = {
            ...state.data,
            results: action.payload.results,
            facets: action.payload.facets || state.data.facets,
            nextSet: action.payload.nextSet || state.data.nextSet
          };
        }
        state.loading = false;
      })
      .addCase(loadMore.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload) {
          state.data = {
            ...state.data,
            ...action.payload,
            results: action.payload.results || []
          };
        }
        state.loading = false;
      })
      .addCase(setFilters.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getToursToken.fulfilled, (state) => ({
        ...state,
        data: {
          ...state.data,
          searchToken: INITIAL_DEAL_FINDER_RESULTS_STATE.data.searchToken,
          expires: INITIAL_DEAL_FINDER_RESULTS_STATE.data.expires
        }
      }))

      // Handle pending states for getPackages with skeleton loading
      .addCase(getPackages.pending, (state) => {
        state.loading = true;
        state.error = false;

        state.data = {
          ...MOCK_SKELETON_LOADING_PACKAGES_RESPONSE.data,
          results: MOCK_SKELETON_LOADING_PACKAGES_RESPONSE.data?.results || [],
          searchToken: state.data.searchToken,
          expires: state.data.expires
        };
      })
      .addCase(performSearch.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addMatcher(
        (action) =>
          [performProductSearch.pending.type, performDestinationSearch.pending.type, setFilters.pending.type].includes(
            action.type
          ),
        (state) => {
          state.loading = true;
          state.error = false;

          // Preserve existing filters when showing skeleton loading
          state.data = {
            ...MOCK_SKELETON_LOADING_PACKAGES_RESPONSE.data,
            results: MOCK_SKELETON_LOADING_PACKAGES_RESPONSE.data?.results || [],
            facets: state.data.facets, // Preserve existing filters
            searchToken: state.data.searchToken,
            expires: state.data.expires
          };
        }
      )

      // Handle rejected states for async actions
      .addMatcher(
        (action) =>
          [
            performSearch.rejected.type,
            performProductSearch.rejected.type,
            performDestinationSearch.rejected.type,
            setFilters.rejected.type,
            getPackages.rejected.type,
            loadMore.rejected.type
          ].includes(action.type),
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = (action.payload as string) || true;
        }
      );
  }
});

export const {
  clearResults,
  setToken,
  searchFailure,
  receiveResultsSuccess,
  receiveResultsFailure,
  receiveTokenSuccess
} = dealFinderResultsSlice.actions;

export const dealFinderResultsActions = { setFilters, loadMore };

export const dealFinderResultsReducer = dealFinderResultsSlice.reducer;
